import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Link, Paper, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { signageService } from '../../services/signageService';
import { ISignageItem, ISignageStore, SIGNAGE_MODES, SIGNAGE_STATES, SignageMode, SignageState } from '../../types/signage';
import ConfirmDialog from './ConfirmDialog';
import ItemEditor from './ItemEditor';
import KioskPreview from './KioskPreview';
import PlaylistList from './PlaylistList';
import StoreSettingsPanel, { applyDraftPatch, draftFromStore, IStoreDraft, isDraftDirty } from './StoreSettingsPanel';
import { playlistNote } from './playback';
import { isDefaultStore, storeDisplayName, storeIdFromSegment } from './storeRoute';

const NEW_ITEM = 'new';

const StorePlaylistPage: React.FC = () => {
  const params = useParams();
  const storeId = storeIdFromSegment(params.storeId);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const canManage = hasRole(['signage:admin', 'admin']);

  const [store, setStore] = useState<ISignageStore | null>(null);
  const [allItems, setAllItems] = useState<ISignageItem[]>([]);
  // The store has one configuration for its entrance device and one for its exit device.
  const [mode, setMode] = useState<SignageMode>('entrance');
  const [state, setState] = useState<SignageState>('waiting');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // On narrow screens the editor sits below the list, so bring it into view.
  useEffect(() => {
    if (selectedId) editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);
  const [playing, setPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<IStoreDraft | null>(null);
  const [savingStore, setSavingStore] = useState(false);
  // The scale being typed for the selected item, so the preview follows it before saving.
  const [draftScale, setDraftScale] = useState<number | undefined>(undefined);
  const [storeError, setStoreError] = useState('');
  const [confirmDeleteStore, setConfirmDeleteStore] = useState(false);

  const load = useCallback(async () => {
    try {
      const [stores, loadedItems] = await Promise.all([signageService.getStores(), signageService.getItems(storeId)]);
      const found = stores.find((candidate) => candidate.storeId === storeId) ?? null;
      setStore(found);
      setAllItems(loadedItems);
      setError(found ? '' : 'This store does not exist.');
    } catch (e: any) {
      setError(typeof e === 'string' ? e : 'Failed to load the playlist');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // The form starts from the saved store and starts over whenever the store is (re)loaded.
  useEffect(() => {
    if (store) setDraft(draftFromStore(store));
  }, [store]);

  const handleSaveStore = async () => {
    if (!store || !draft) return;
    setSavingStore(true);
    setStoreError('');
    try {
      await signageService.updateStore(store.storeId, isDefaultStore(store.storeId) ? '' : draft.name.trim(), { entrance: draft.entrance, exit: draft.exit });
      showToast('Store settings saved', 'success');
      await load();
    } catch (e: any) {
      setStoreError(typeof e === 'string' ? e : 'Failed to save the store');
    } finally {
      setSavingStore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setSelectedId(null);
    load();
  }, [load]);

  // Everything below works on the items of the selected mode and state only.
  const inView = (item: ISignageItem) => item.mode === mode && item.state === state;
  const items = useMemo(() => allItems.filter((item) => item.mode === mode && item.state === state), [allItems, mode, state]);
  const setItems = (update: (previous: ISignageItem[]) => ISignageItem[]) =>
    setAllItems((previous) => [...previous.filter((item) => !inView(item)), ...update(previous.filter(inView))]);
  const stateInfo = SIGNAGE_STATES.find((candidate) => candidate.value === state)!;

  const handleToggle = async (item: ISignageItem, enabled: boolean) => {
    setItems((previous) => previous.map((candidate) => (candidate.id === item.id ? { ...candidate, enabled } : candidate)));
    try {
      await signageService.setItemEnabled(item.id, enabled);
    } catch (e: any) {
      showToast(typeof e === 'string' ? e : 'Failed to update the item', 'error');
      load();
    }
  };

  const handleReorder = async (next: ISignageItem[]) => {
    setItems(() => next);
    try {
      await signageService.setOrder(storeId, mode, state, next.map((item) => item.id));
    } catch (e: any) {
      showToast(typeof e === 'string' ? e : 'Failed to save the order', 'error');
      load();
    }
  };

  const setAllEnabled = async (enabled: boolean) => {
    const changed = items.filter((item) => item.enabled !== enabled);
    setItems((previous) => previous.map((item) => ({ ...item, enabled })));
    try {
      await Promise.all(changed.map((item) => signageService.setItemEnabled(item.id, enabled)));
    } catch (e: any) {
      showToast(typeof e === 'string' ? e : 'Failed to update the items', 'error');
      load();
    }
  };

  const handleDeleteStore = async () => {
    setConfirmDeleteStore(false);
    try {
      await signageService.deleteStore(storeId);
      showToast('Store deleted', 'success');
      navigate('/signage');
    } catch (e: any) {
      showToast(typeof e === 'string' ? e : 'Failed to delete the store', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!store) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" action={<Button onClick={() => navigate('/signage')}>Back to stores</Button>}>
          {error || 'This store does not exist.'}
        </Alert>
      </Box>
    );
  }

  const name = storeDisplayName(store.storeId, store.name);
  const note = playlistNote(items, isDefaultStore(storeId));
  const selected = selectedId && selectedId !== NEW_ITEM ? items.find((item) => item.id === selectedId) ?? null : null;
  const visibleCount = items.filter((item) => item.enabled).length;
  // The preview shows the form as it is being edited, saved or not.
  const settingsDirty = !!draft && isDraftDirty(draft, store);
  const modeSettings = draft?.[mode] ?? store[mode];
  const previewSettings = {
    orientation: modeSettings?.orientation ?? 'portrait',
    playAudio: modeSettings?.playAudio ?? false,
  };
  const modeLabel = SIGNAGE_MODES.find((option) => option.value === mode)!.label;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/signage" underline="hover" color="inherit">
          Stores
        </Link>
        <Typography color="text.primary">{isDefaultStore(storeId) ? 'Default' : `${store.storeId} · ${store.name}`}</Typography>
      </Breadcrumbs>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {name}
        </Typography>
        <Chip size="small" label={isDefaultStore(storeId) ? 'DEFAULT' : store.storeId} sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isDefaultStore(storeId)
          ? 'Stores that have no visible items of their own for a state show this playlist for that state.'
          : 'Choose the entrance or the exit device, set it up below, then add its media for each state. The preview shows your changes even before you save the settings.'}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="subtitle2">Configuration for the</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, value: SignageMode | null) => {
            if (!value) return;
            setMode(value);
            setSelectedId(null);
            setDraftScale(undefined);
          }}
        >
          {SIGNAGE_MODES.map((option) => (
            <ToggleButton key={option.value} value={option.value} sx={{ px: 2.5, gap: 1, textTransform: 'none', fontWeight: 600 }}>
              {option.value === 'entrance' ? <LoginIcon fontSize="small" /> : <LogoutIcon fontSize="small" />}
              {`${option.label} (${allItems.filter((item) => item.mode === option.value).length})`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          The same store can show different media and use different device settings at the entrance and at the exit.
        </Typography>
      </Stack>

      {draft && (
        <StoreSettingsPanel
          store={store}
          isDefault={isDefaultStore(storeId)}
          canManage={canManage}
          mode={mode}
          draft={draft}
          dirty={settingsDirty}
          saving={savingStore}
          error={storeError}
          onChange={(patch) => {
            setDraft((previous) => (previous ? applyDraftPatch(previous, mode, patch) : previous));
            setStoreError('');
          }}
          onSave={handleSaveStore}
          onReset={() => setDraft(draftFromStore(store))}
          onDelete={() => setConfirmDeleteStore(true)}
        />
      )}

      <Tabs
        value={state}
        onChange={(_, value: SignageState) => {
          setState(value);
          setSelectedId(null);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {SIGNAGE_STATES.map((option) => (
          <Tab key={option.value} value={option.value} label={`${option.label} (${allItems.filter((item) => item.mode === mode && item.state === option.value).length})`} />
        ))}
      </Tabs>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(300px, 400px) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        {/* The editor opens directly under the list of media, left of the preview. */}
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {stateInfo.label} · {visibleCount}/{items.length} visible
              </Typography>
              {canManage && items.length > 0 && (
                <>
                  <Button size="small" onClick={() => setAllEnabled(true)}>
                    Show all
                  </Button>
                  <Button size="small" onClick={() => setAllEnabled(false)}>
                    Hide all
                  </Button>
                </>
              )}
              {canManage && (
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setSelectedId(NEW_ITEM)}>
                  Add media
                </Button>
              )}
            </Stack>
            {items.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 600 }}>No media yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isDefaultStore(storeId)
                    ? `Add an image, GIF or video. ${stateInfo.hint}`
                    : `Until you add items this store shows the Default playlist for “${stateInfo.label}”.`}
                </Typography>
              </Box>
            ) : (
              <PlaylistList items={items} selectedId={selectedId} canManage={canManage} onSelect={setSelectedId} onToggle={handleToggle} onReorder={handleReorder} />
            )}
          </Paper>
          <Paper ref={editorRef} sx={{ borderRadius: 2, p: 2, minWidth: 0 }}>
            {selectedId === NEW_ITEM || selected ? (
              <ItemEditor
                key={selected?.id ?? NEW_ITEM}
                storeId={storeId}
                mode={mode}
                state={state}
                item={selected}
                canManage={canManage}
                orientation={previewSettings.orientation}
                onScaleChange={setDraftScale}
                onCancel={() => setSelectedId(null)}
                onSaved={(saved) => {
                  setAllItems((previous) => (previous.some((item) => item.id === saved.id) ? previous.map((item) => (item.id === saved.id ? saved : item)) : [...previous, saved]));
                  setMode(saved.mode);
                  setState(saved.state);
                  setSelectedId(saved.id);
                  showToast('Saved', 'success');
                  load();
                }}
                onDeleted={(id) => {
                  setAllItems((previous) => previous.filter((item) => item.id !== id));
                  setSelectedId(null);
                  showToast('Deleted', 'success');
                  load();
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {canManage ? 'Select an item in the playlist to edit it, or press Add media.' : 'Select an item in the playlist to see its details.'}
              </Typography>
            )}
          </Paper>
        </Stack>
        <Paper sx={{ borderRadius: 2, p: 2, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Kiosk preview
            </Typography>
            <Button size="small" startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />} onClick={() => setPlaying((previous) => !previous)}>
              {playing ? 'Pause' : 'Play'}
            </Button>
          </Stack>
          <KioskPreview items={items} selectedId={selectedId === NEW_ITEM ? null : selectedId} playing={playing} orientation={previewSettings.orientation} scaleOverride={selected && draftScale !== undefined ? { itemId: selected.id, scale: draftScale } : undefined} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }} data-testid="kiosk-preview-settings">
            {modeLabel} device · {previewSettings.orientation === 'landscape' ? 'landscape' : 'portrait'} · each media has its own scale · videos {previewSettings.playAudio ? 'play their sound on the kiosk (muted here)' : 'are muted'}
            {settingsDirty ? ' · unsaved changes' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            GIF loops are estimated at 4 s in this preview. Pause to inspect the selected item.
          </Typography>
          {note && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {note}
            </Alert>
          )}
        </Paper>
      </Box>

      <ConfirmDialog
        open={confirmDeleteStore}
        title="Delete store"
        message={`Delete ${store.storeId} and its ${allItems.length} media item(s)? Uploaded files are removed too. This cannot be undone.`}
        onConfirm={handleDeleteStore}
        onClose={() => setConfirmDeleteStore(false)}
      />
    </Box>
  );
};

export default StorePlaylistPage;
